import { createSlice, original } from '@reduxjs/toolkit';
import { NAMESPACE } from '../namespace';

/**
 * @typedef {object} BotspecsState
 * @property {string} selectedBotspec
 * @property {string} selectedFolder
 * @property {string} selectedVariableGroup
 * @property {string} selectedFunctionGroup
 * @property {string} selectedStorageGroup
 * @property {Record<string, any>} botspecs Flujos por id.
 * @property {Record<string, any>} folders Carpetas por id.
 * @property {boolean} parsing
 * @property {boolean} loadingBotspecs
 * @property {string[]} parsingErrors
 * @property {{ botspecs?: Record<string, any> }} past Botspecs antes del último cambio.
 * @property {any[]} templates
 * @property {any[]} chatbots
 * @property {any[]} currentBusinessUnits
 * @property {Record<string, any>} globalVariables
 * @property {Record<string, any>} globalFunctions
 * @property {boolean} loadingGlobalVariables
 * @property {boolean} loadingGlobalFunctions
 */

/** @returns {BotspecsState} */
const initialState = () => ({
  selectedBotspec: '',
  selectedFolder: '',
  selectedVariableGroup: '',
  selectedFunctionGroup: '',
  selectedStorageGroup: '',
  botspecs: {},
  folders: {},
  parsing: false,
  loadingBotspecs: true,
  parsingErrors: [],
  past: {},
  templates: [],
  chatbots: [],
  currentBusinessUnits: [],
  globalVariables: {},
  globalFunctions: {},
  loadingGlobalVariables: false,
  loadingGlobalFunctions: false,
});

/** @template P @typedef {{ type: string, payload: P }} PayloadOf */

// Acción con varios argumentos: el payload es un objeto con esos nombres.
const withArgs = (...names) => (...args) => ({
  payload: Object.fromEntries(names.map((name, index) => [name, args[index]])),
});

// Flujos (botspecs), carpetas, chatbots publicados, plantillas y datos globales de la
// compañía. Los reducers usan Immer: se escriben como mutaciones sobre el borrador.
const botspecsSlice = createSlice({
  name: NAMESPACE,
  initialState,
  reducers: {
    resetChatbots: () => initialState(),

    updateSelectedBotspec: (state, { payload: id }) => { state.selectedBotspec = id; },
    updateSelectedFolder: (state, { payload: folderId }) => { state.selectedFolder = folderId; },
    updateSelectedVariableGroup: (state, { payload: groupId }) => {
      state.selectedVariableGroup = groupId;
    },
    updateSelectedFunctionGroup: (state, { payload: groupId }) => {
      state.selectedFunctionGroup = groupId;
    },
    updateSelectedStorageGroup: (state, { payload: groupId }) => {
      state.selectedStorageGroup = groupId;
    },
    resetSelections: (state) => {
      state.selectedVariableGroup = '';
      state.selectedFunctionGroup = '';
      state.selectedStorageGroup = '';
    },

    // Se guarda la versión anterior de los botspecs para poder deshacer
    // (restoreBotspecPastState).
    updateBotspecs: (state, { payload: botspecs }) => {
      state.past = { botspecs: original(state.botspecs) };
      state.botspecs = botspecs;
    },
    restoreBotspecPastState: (state, { payload: id }) => {
      state.botspecs[id] = { ...state.past.botspecs[id] };
    },
    updateParsingBotspec: (state, { payload: value }) => { state.parsing = value; },
    updateLoadingBotspecs: (state, { payload: value }) => { state.loadingBotspecs = value; },
    updateBotspecParsingErrors: (state, { payload: errors }) => { state.parsingErrors = errors; },

    loadingBotErrorGroups: {
      reducer: (state, /** @type {PayloadOf<{ id: string, loading: boolean }>} */ { payload: { id, loading } }) => {
        state.past = { botspecs: original(state.botspecs) };
        state.botspecs[id] = { ...state.botspecs[id], loadingErrorGroups: loading };
      },
      prepare: withArgs('id', 'loading'),
    },
    updateBotErrorGroups: {
      reducer: (state, /** @type {PayloadOf<{ id: string, errorsGroups: any }>} */ { payload: { id, errorsGroups } }) => {
        state.botspecs[id] = {
          ...state.botspecs[id], errorsInfo: errorsGroups, loadingErrorGroups: false,
        };
      },
      prepare: withArgs('id', 'errorsGroups'),
    },
    loadingBotErrorsDetails: {
      reducer: (state, /** @type {PayloadOf<{ id: string, loading: boolean }>} */ { payload: { id, loading } }) => {
        state.botspecs[id].errorsInfo = { ...state.botspecs[id].errorsInfo, loadingDetails: loading };
      },
      prepare: withArgs('id', 'loading'),
    },
    updateBotErrorsDetails: {
      reducer: (state, /** @type {PayloadOf<{ id: string, gid: string, errorsDetails: any }>} */ { payload: { id, gid, errorsDetails } }) => {
        const { errorsInfo } = state.botspecs[id];
        const group = errorsInfo.errors.find((errorGroup) => errorGroup.groupId === gid);
        group.details = { ...group.details, ...errorsDetails };
        errorsInfo.loadingDetails = false;
      },
      prepare: withArgs('id', 'gid', 'errorsDetails'),
    },

    addFolder: (state, { payload: folder }) => { state.folders[folder.id] = folder; },
    updateFolders: (state, { payload: folders }) => { state.folders = { ...folders }; },
    updateStateTemplates: (state, { payload: templates }) => { state.templates = [...templates]; },
    updateChatbots: (state, { payload: chatbots }) => { state.chatbots = [...chatbots]; },
    updateCurrentBUnits: (state, { payload: units }) => { state.currentBusinessUnits = [...units]; },

    updateGlobalVariables: (state, { payload: variables }) => {
      state.globalVariables = { ...variables };
      state.loadingGlobalVariables = false;
    },
    updateGlobalFunctions: (state, { payload: functions }) => {
      state.globalFunctions = { ...functions };
      state.loadingGlobalFunctions = false;
    },
    loadingGlobalVariables: (state, { payload: loading }) => { state.loadingGlobalVariables = loading; },
    loadingGlobalFunctions: (state, { payload: loading }) => { state.loadingGlobalFunctions = loading; },
  },
});

export const botspecsActions = botspecsSlice.actions;

export default { [NAMESPACE]: botspecsSlice.reducer };
